import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Search, X } from "lucide-react";

import { INDUSTRIES } from "@/data/industries";
export type { IndustryItem, IndustryGroup } from "@/data/industries";

interface IndustryPickerProps {
  selected: string | null;
  onSelect: (id: string | null) => void;
  selectedSubtypes: string[];
  onSubtypesChange: (subtypes: string[]) => void;
  disabled?: boolean;
}

export function IndustryPicker({ selected, onSelect, selectedSubtypes, onSubtypesChange, disabled }: IndustryPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showSub, setShowSub] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return INDUSTRIES;
    const q = search.toLowerCase();
    return INDUSTRIES
      .map(g => ({
        ...g,
        items: g.items.filter(i =>
          i.label.toLowerCase().includes(q) ||
          i.subtypes.some(s => s.toLowerCase().includes(q))
        ),
      }))
      .filter(g => g.items.length);
  }, [search]);

  const sel = useMemo(() => {
    for (const g of INDUSTRIES) {
      const f = g.items.find(i => i.id === selected);
      if (f) return f;
    }
    return null;
  }, [selected]);

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={cn(
          "flex items-center justify-between w-full px-3.5 py-2.5 rounded-lg border text-sm min-h-[44px] transition-all cursor-pointer bg-background",
          open ? "border-foreground" : "border-input",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {selected && sel ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="font-medium text-foreground truncate">{sel.label}</span>
            {selectedSubtypes.length > 0 && (
              <span className="text-[11px] font-semibold text-primary-foreground bg-muted-foreground/70 px-2 py-0.5 rounded-full">
                {selectedSubtypes.length}
              </span>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(null);
                onSubtypesChange([]);
                setShowSub(false);
              }}
              className="ml-auto flex items-center justify-center w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <span className="text-muted-foreground">Search or select industry...</span>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-2" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-popover border border-border rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border text-muted-foreground">
            <Search className="w-3.5 h-3.5 shrink-0" />
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Type to search..."
              className="border-none outline-none flex-1 text-sm text-foreground bg-transparent placeholder:text-muted-foreground/60"
            />
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {filtered.map(g => (
              <div key={g.group}>
                <div className="px-3.5 pt-2.5 pb-1 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wider sticky top-0 bg-popover">
                  {g.group}
                </div>
                {g.items.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      onSelect(item.id);
                      onSubtypesChange([]);
                      setSearch("");
                      setOpen(false);
                      setShowSub(true);
                    }}
                    className={cn(
                      "flex justify-between items-center w-full px-3.5 py-2 pl-5 text-sm cursor-pointer transition-colors text-left",
                      selected === item.id
                        ? "bg-accent/40 font-medium text-foreground"
                        : "text-foreground/80 hover:bg-muted/60"
                    )}
                  >
                    <span>{item.label}</span>
                    <span className="text-[11px] text-muted-foreground/50">{item.subtypes.length}</span>
                  </button>
                ))}
              </div>
            ))}
            {!filtered.length && (
              <div className="py-5 text-center text-sm text-muted-foreground">No match</div>
            )}
          </div>
        </div>
      )}

      {/* Subtypes */}
      {selected && sel && (showSub || selectedSubtypes.length > 0) && (
        <div className="mt-2 p-3 bg-muted/30 border border-border/50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Sub-type</span>
            <button
              type="button"
              onClick={() =>
                selectedSubtypes.length === sel.subtypes.length
                  ? onSubtypesChange([])
                  : onSubtypesChange([...sel.subtypes])
              }
              className="text-[11px] font-semibold text-foreground underline underline-offset-2 hover:text-foreground/80"
            >
              {selectedSubtypes.length === sel.subtypes.length ? "Clear all" : "Select all"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {sel.subtypes.map(st => {
              const on = selectedSubtypes.includes(st);
              return (
                <button
                  key={st}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    on
                      ? onSubtypesChange(selectedSubtypes.filter(s => s !== st))
                      : onSubtypesChange([...selectedSubtypes, st])
                  }
                  className={cn(
                    "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium cursor-pointer transition-all whitespace-nowrap",
                    on
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-background text-muted-foreground hover:border-foreground/30"
                  )}
                >
                  {on && <Check className="w-2.5 h-2.5" />}
                  {st}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
